/**
 * **The summary column's entry point.** Everything the standing left column of KANON view 04 draws,
 * decided in one place: the identity block, the trainer's note, attendance, the active plan and the
 * coaching goal.
 *
 * ── Why one function and not five ──────────────────────────────────────────────────────────────
 * The column is one surface with one rule running through it: a section that has no data does not
 * render, and a section whose read FAILED is not the same thing as a section with nothing in it.
 * Five exported builders would put that rule in the component, five times, and a component holding
 * five projections and combining them is the combination `CLAUDE.md` says to move behind the entry
 * point where a test can reach it.
 *
 * What the component receives is one object with five nullable members and no decisions left in it.
 *
 * ── Every source is nullable, and each nullability means something different ───────────────────
 * `detail` absent is the whole column absent — there is no client. `goal` absent means the client
 * has no active goal, which the canon draws as a card with a sentence rather than as a blank. `note`
 * absent means the trainer has written none. `consistency` absent means the attendance read has not
 * landed or failed, and the card says so instead of drawing an empty strip that reads as "missed
 * everything".
 */

import {
  goalFigureText,
  goalMeasurementTab,
  goalTypeOf,
  planCardWords,
  type ClientStanding,
  type GoalType,
  type PlanCardWords,
} from '~/entities/client';
import { planCategoryKey, type PlanCategoryKey } from '~/entities/plan-draft';
import { addDays, parseIsoDate } from '~/shared/lib/calendarDate';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import { clampPercent } from '~/shared/lib/percent';

import { type AttendanceDay, type AttendanceView, buildAttendance } from './attendance';
import { clientSinceLabel } from './clientSince';
import type { ClientDetailTab } from './detailTabs';

export type ClientDetailStatus = 'Active' | 'Invited';

/** What `GET /customer/{id}/detail` gives, narrowed at the API seam. `ui/` never sees a DTO. */
export interface DetailSource {
  readonly id: number;
  readonly fullName: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
  readonly status: ClientDetailStatus | null;
  readonly sinceDate: string | null;
  readonly plan: PlanSource | null;
}

export interface PlanSource {
  readonly planTemplateId: number | null;
  readonly assignmentId: number | null;
  readonly name: string;
  /** The plan runs at the client's own pace, so the two counts below are CYCLES, not weeks. */
  readonly selfPaced: boolean;
  readonly weeksDone: number;
  readonly weeksTotal: number;
  readonly endsInDays: number | null;
  readonly paused: boolean;
  readonly coverImageUrl: string | null;
  readonly category: string | null;
}

export interface NoteSource {
  readonly body: string;
  /** The backend's own rendered "zmieniona 7 sierpnia". Printed as it arrives; never re-derived. */
  readonly updatedText: string | null;
}

export interface GoalSource {
  readonly id: number;
  readonly title: string;
  /** The contract's `PrBased | SessionCount | WeightTarget | Custom`. Decides what `pct` MEANS. */
  readonly type: string | null;
  readonly status: string | null;
  readonly deadline: string | null;
  readonly daysToDeadline: number | null;
  readonly targetValue: number | null;
  readonly targetUnit: string | null;
  readonly startValue: number | null;
  readonly currentValue: number | null;
  readonly pct: number | null;
}

export interface SummarySources {
  readonly detail: DetailSource | null;
  readonly note: NoteSource | null;
  readonly consistency: readonly AttendanceDay[] | null;
  /**
   * Where a self-paced client stands (R16), and `null` for every dated one — in which case the
   * consistency fold answers instead. Already decided by `entities/client`; this column only
   * chooses which card to build from it.
   */
  readonly standing: ClientStanding | null;
  readonly goal: GoalSource | null;
}

export interface SummaryDeps {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly locale: string;
  readonly now?: Date;
}

export interface IdentityView {
  readonly name: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
  readonly statusLabel: string;
  readonly active: boolean;
  readonly sinceLabel: string | undefined;
}

export interface NoteView {
  readonly body: string;
  readonly hasBody: boolean;
  readonly updatedText: string | null;
  readonly placeholder: string;
}

/**
 * The backend's own `[MaxLength(10_000)]` on `UpsertCustomerNoteRequest.Body`, mirrored.
 *
 * The same rule the measurement form follows and the session comment already followed: a value this
 * app accepts is one the server accepts, so the trainer never discovers a bound by having a long
 * note rejected after writing it. This was the third field in the app to be missing one.
 */
export const CLIENT_NOTE_MAX_LENGTH = 10_000;

export interface PlanView {
  readonly assignmentId: number | null;
  readonly name: string;
  readonly coverImageUrl: string | null;
  /**
   * The category as a KEY, which is what picks the pill's hue. Carried beside the WORDS rather than
   * derived at the call site for the reason `planLibraryRow` gives: the key is the domain value and
   * the label is the trainer's word for it, and a component that narrowed the string itself would
   * be a second place the six categories are known. `null` for a category this build has no colour
   * for, which takes the neutral pill.
   */
  readonly categoryKey: PlanCategoryKey | null;
  /**
   * Everything the card SAYS — `entities/client`'s `planCardWords`, which the roster's preview
   * panel reads too. The card's drawing was shared on 1 September 2026 and its copy was not, so
   * both surfaces composed these six strings from the same keys, separately; all four ways the two
   * cards had drifted were words.
   */
  readonly words: PlanCardWords;
  readonly paused: boolean;
  /**
   * 0–100. The bar is clamped here so no stylesheet has to guard a width.
   *
   * The plain ratio of `weeksDone` to `weeksTotal`. KANON „Karty zadań" (24 September 2026) stands
   * its bar in the middle of the week the plan is IN, but no field on the wire means that week:
   * `weeksDone` counts weeks with a finished workout, so a half-week step back from it drew a plan
   * trained through all four of four weeks at 88% and a one-week plan at 50%.
   */
  readonly progressPct: number;
  /** „do 18 paź" — the day the plan ends, where the header read says how far off that is. */
  readonly endDateLabel: string | null;
}

export interface GoalFigure {
  readonly value: string;
  readonly label: string;
}

/**
 * A measurable goal whose measurement has never been taken.
 *
 * The reason this is its own shape rather than a flag: the card has to say WHICH reading is missing
 * and where the trainer takes it, and that differs by goal type. It is `null` on every goal whose
 * bar means something.
 */
export interface GoalMeasurementGap {
  /** "Brak pomiaru" — one line under the track, saying why it is empty. */
  readonly note: string;
  /** The tab that would produce the missing reading. */
  readonly tab: ClientDetailTab;
  readonly linkLabel: string;
}

export interface GoalView {
  readonly id: number;
  readonly title: string;
  /** 0–100, clamped. `null` where the goal carries no measurable progress at all. */
  readonly progressPct: number | null;
  /**
   * Set where the goal HAS a metric and nobody has recorded one, in which case `progressPct` is
   * `null` rather than the zero the backend sends. See `measurementGapOf`.
   */
  readonly gap: GoalMeasurementGap | null;
  /** start · now · target. Absent members are dropped rather than printed as a dash. */
  readonly figures: readonly GoalFigure[];
  readonly deadlineLabel: string | null;
}

export interface SummaryColumnView {
  readonly identity: IdentityView | null;
  readonly note: NoteView;
  /**
   * The frekwencja fold — `null` while the read is in flight, on a failed read, AND for every
   * self-paced client, whose adherence R15 forbids. `selfPaced` says which of those it is.
   */
  readonly attendance: AttendanceView | null;
  /**
   * The client's plan runs at their own pace, so the standing card takes the attendance card's
   * place. This is the MODE, read off the plan, and it is what decides which card is drawn.
   */
  readonly selfPaced: boolean;
  /**
   * The figures for that card, or `null` when the compliance read has not landed or failed. It
   * decides only what the standing card CONTAINS, never whether it is shown — see the note on the
   * suppression below.
   */
  readonly standing: ClientStanding | null;
  readonly plan: PlanView | null;
  readonly goal: GoalView | null;
}

/**
 * A measured quantity with the label it wears, or `null` where nobody has measured it.
 *
 * The FORMATTING is `entities/client`'s `goalFigureText`, which the roster preview's goal card
 * reads too — the two used to spell one rule twice and had already parted on the unit fallback.
 * What stays here is this card's own decision about an absent measurement: it drops the figure,
 * where the panel prints a dash.
 */
function figure(
  value: number | null,
  unit: string | null,
  label: string,
  t: SummaryDeps['t'],
  locale: string,
): GoalFigure | null {
  const text = goalFigureText(value, unit, { t, locale });
  return text === null ? null : { value: text, label };
}

/**
 * "Brak pomiaru" and the tab that fills it, where the goal HAS a metric and nobody recorded one —
 * `entities/client`'s `goalMeasurementTab`, which the goal task card reads too. `Custom` and
 * `SessionCount` are never a gap; see there.
 */
function measurementGapOf(
  type: GoalType | null,
  currentValue: number | null,
  t: SummaryDeps['t'],
): GoalMeasurementGap | null {
  const tab = goalMeasurementTab(type, currentValue);
  if (tab === null) return null;
  return {
    note: t('web:clientDetail.goal.unmeasured'),
    tab,
    linkLabel: t(`web:clientDetail.goal.unmeasuredLink.${tab}`),
  };
}

/**
 * Does this client's plan run at their own pace?
 *
 * The **one** place that question is answered, because two surfaces ask it and they must not answer
 * it differently: this column decides which card to draw, and `api/` decides whether to fetch the
 * standing at all. Answered off the PLAN — the mode is a property of the plan, arrives with the
 * header read that gates the whole screen, and no request outcome can change it.
 *
 * `null` is "not yet known", not "dated", and callers must treat it that way. A client with no
 * active plan is likewise not self-paced: there is no undated schedule for the adherence fold to
 * misread, and the standing card would have nothing to say.
 */
export function isSelfPacedPlan(detail: DetailSource | null): boolean {
  return detail?.plan?.selfPaced === true;
}

/** „18 paź" — the column's short date. */
const SHORT_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };

export function buildSummaryColumn(
  sources: SummarySources,
  deps: SummaryDeps,
): SummaryColumnView {
  const { t, locale } = deps;
  const now = deps.now ?? new Date();
  const { detail, note, consistency, standing, goal } = sources;
  const selfPaced = isSelfPacedPlan(detail);

  const identity: IdentityView | null =
    detail === null
      ? null
      : {
          name: detail.fullName,
          initials: detail.initials,
          avatarUrl: detail.avatarUrl,
          // The contract's two statuses, and only two — an ended relationship leaves the roster
          // entirely (handoff A4), so there is no third word to fall back to.
          statusLabel:
            detail.status === 'Invited' ? t('clients.status.invited') : t('clients.status.active'),
          active: detail.status !== 'Invited',
          sinceLabel: clientSinceLabel(detail.sinceDate, t, now),
        };

  const noteView: NoteView = {
    body: note?.body ?? '',
    hasBody: (note?.body ?? '').trim().length > 0,
    updatedText: note?.updatedText ?? null,
    placeholder: t('clients.detail.overview.note.placeholder'),
  };

  const planSource = detail?.plan ?? null;
  const plan: PlanView | null =
    planSource === null
      ? null
      : {
          assignmentId: planSource.assignmentId,
          name: planSource.name,
          coverImageUrl: planSource.coverImageUrl,
          categoryKey: planCategoryKey(planSource.category),
          words: planCardWords(planSource, t),
          paused: planSource.paused,
          progressPct:
            planSource.weeksTotal <= 0
              ? 0
              : (clampPercent((planSource.weeksDone / planSource.weeksTotal) * 100) ?? 0),
          endDateLabel:
            planSource.endsInDays == null
              ? null
              : t('web:clientDetail.plan.endDate', {
                  date: dateTimeFormat(locale, SHORT_DATE).format(addDays(now, planSource.endsInDays)),
                }),
        };

  const goalType = goal === null ? null : goalTypeOf(goal.type);
  const gap = goal === null ? null : measurementGapOf(goalType, goal.currentValue, t);
  const goalView: GoalView | null =
    goal === null
      ? null
      : {
          id: goal.id,
          title: goal.title,
          // An unmeasured goal has no percentage, whatever the wire says. The card draws the track
          // as unmeasured instead, and a `progressbar` with no `aria-valuenow` is what ARIA already
          // means by indeterminate.
          progressPct: gap === null ? clampPercent(goal.pct) : null,
          gap,
          figures: [
            figure(goal.startValue, goal.targetUnit, t('clients.detail.overview.goal.start'), t, locale),
            figure(goal.currentValue, goal.targetUnit, t('clients.detail.overview.goal.now'), t, locale),
            figure(goal.targetValue, goal.targetUnit, t('clients.detail.overview.goal.target'), t, locale),
          ].filter((entry): entry is GoalFigure => entry !== null),
          deadlineLabel:
            goal.deadline == null
              ? null
              : t('clients.detail.overview.goal.deadline', {
                  // Short, since KANON „Karty zadań" moved the deadline into the card's head beside
                  // its label: „termin 18 paź · za 25 dni".
                  // The deadline is a civil day the wire stamps at midnight, so its date part is read
                  // verbatim: through `new Date` it is UTC midnight, a day early west of Greenwich.
                  date: dateTimeFormat(locale, SHORT_DATE).format(
                    parseIsoDate(goal.deadline.slice(0, 10)) ?? new Date(goal.deadline),
                  ),
                  days: goal.daysToDeadline ?? 0,
                }),
        };

  return {
    identity,
    note: noteView,
    // ── The whole attendance card goes, not just its percentage ────────────────────────────────
    // R15 forbids a plan-relative adherence figure for a self-paced client on any surface, and this
    // card is one from end to end: the sentence is a ratio, and the strip's marks are `wykonany` /
    // `pominięty` / `bez treningu` against a schedule this mode does not have. `/consistency` has no
    // self-paced awareness at all — an undated plan produces no missed day, so the fold would report
    // a flawless 100% for exactly the client the mode exists to stop scoring. Suppressing the
    // percentage alone would leave a strip whose legend names two states that can never occur.
    //
    // ── Keyed on the MODE, never on the standing ───────────────────────────────────────────────
    // The mode is a property of the PLAN and arrives with `detail`. The standing comes from
    // `/compliance` and is populated for an ACTIVE assignment only, which made it wrong on three
    // counts when it decided this: a self-paced plan in any non-active state fell through to the
    // 100% fold; a FAILED compliance read left it null forever and did the same, silently, with
    // `attendanceFailed` false because that flag reads the consistency query; and even on the happy
    // path `/consistency` resolves first, so the fold painted 100% and then swapped.
    //
    // ── An UNKNOWN mode is not a dated one ─────────────────────────────────────────────────────
    // `detail === null` is the window before the header read lands, and this column renders
    // throughout it. Letting a falsy `selfPaced` stand in for "dated" there put the whole defect
    // back on a timer: the header is quicker than `/consistency` only MOST of the time (see the
    // note on `attendanceFailed`), and on the runs where it is not, a resolved consistency folds to
    // a flawless 100% for a self-paced client before anyone knows the mode. So the fold is withheld
    // until the mode is known, and the card draws its ordinary loading state meanwhile — which
    // costs a dated client nothing, because its own read has not landed either.
    attendance:
      detail === null || selfPaced || consistency === null ? null : buildAttendance(consistency),
    selfPaced,
    standing,
    plan,
    goal: goalView,
  };
}
